import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { api } from '../api';
import { useMe } from '../App';
import { findPerm } from '../perm';
import OdooListView from '../views/OdooListView';
import OdooKanban from '../views/OdooKanban';
import '../views/odoo-views.css';

const STATUS_STYLE = {
  planning: { background: '#F3EFE7', color: '#8A7B5E' },
  active: { background: '#E9F0F7', color: '#3A6A96' },
  completed: { background: '#E9F3EC', color: '#3B7A4E' },
};
const COLUMNS = [
  { value: 'planning', label: 'Planning', color: '#B99A5B' },
  { value: 'active', label: 'Active', color: '#3A6A96' },
  { value: 'completed', label: 'Completed', color: '#3B7A4E' },
];
export function money(n) { return '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 }); }

// Deadline signal for a campaign: soonest task due date drives overdue / due-soon.
function deadlineBadge(c) {
  if (c.overdue_count > 0) {
    return <span className="okb-badge" style={{ background: '#FBEEEA', color: '#B4432E' }}>{c.overdue_count} overdue</span>;
  }
  // due within 3 days? use days_to_due if backend provides, else derive from end_date
  let dd = c.days_to_due;
  if (dd == null && c.end_date) {
    dd = Math.ceil((new Date(c.end_date) - new Date()) / 86400000);
  }
  if (dd != null && dd >= 0 && dd <= 3) {
    return <span className="okb-badge" style={{ background: '#FDF3E3', color: '#A87B2D' }}>due in {dd}d</span>;
  }
  return null;
}

export default function Campaigns() {
  const me = useMe();
  const nav = useNavigate();
  const perm = findPerm(me.modules, 'campaigns');
  const [rows, setRows] = useState(null);
  const [brands, setBrands] = useState([]);
  const [modal, setModal] = useState(false);
  const [view, setView] = useState('list');
  const [toast, setToast] = useState('');

  async function load() {
    try { setRows(await api('/campaigns')); } catch (e) { setToast(e.message); }
  }
  useEffect(() => { load(); }, []);
  useEffect(() => { api('/brands/options').then(setBrands).catch(() => {}); }, []);
  function flash(m) { setToast(m); setTimeout(() => setToast(''), 2600); }

  // drag a card to a new column -> update campaign status
  async function moveStatus(c, newStatus) {
    // optimistic update
    setRows((rs) => rs.map((x) => x.id === c.id ? { ...x, status: newStatus } : x));
    try {
      await api('/campaigns/' + c.id, { method: 'PUT', body: {
        name: c.name, objective: c.objective, brand_id: c.brand_id,
        status: newStatus, start_date: c.start_date, end_date: c.end_date, targets: [],
      } });
      flash(`Moved to ${newStatus}`);
      load();
    } catch (e) { flash(e.message); load(); }
  }

  const columns = [
    { key: 'name', label: 'Campaign', render: (c) => (
      <span style={{ fontWeight: 600 }}>{c.name}
        {c.overdue_count > 0 && <span className="olv-pill" style={{ background: '#FBEEEA', color: '#B4432E', marginLeft: 8 }}>{c.overdue_count} overdue</span>}
      </span>) },
    { key: 'brand_name', label: 'Brand', render: (c) => <span style={{ color: '#69727F' }}>{c.brand_name || '—'}</span> },
    { key: 'status', label: 'Status', render: (c) => <span className="olv-pill" style={STATUS_STYLE[c.status]}>{c.status}</span> },
    { key: 'tasks', label: 'Tasks', render: (c) => <span className="olv-pill" style={{ background: '#EEF0F3', color: '#5A6472' }}>{c.done_count}/{c.task_count}</span> },
    { key: 'total_cost', label: 'Total cost', render: (c) => <span style={{ fontWeight: 600 }}>{money(c.total_cost)}</span> },
    { key: 'end_date', label: 'End date', render: (c) => <span style={{ color: '#69727F' }}>{c.end_date ? c.end_date.slice(0, 10) : '—'}</span> },
  ];
  const filters = [
    { key: 'brand_name', label: 'Brand', options: brands.map((b) => ({ value: b.label, label: b.label })) },
    { key: 'status', label: 'Status', options: COLUMNS.map((c) => ({ value: c.value, label: c.label })) },
  ];
  const groupBy = [{ key: 'status', label: 'Status' }, { key: 'brand_name', label: 'Brand' }];

  function renderCard(c) {
    return (
      <>
        <div className="okb-card-title">{c.name}</div>
        <div className="okb-card-row">
          <span>{c.brand_name || '—'}</span>
          <span>·</span>
          <span>{c.done_count}/{c.task_count} tasks</span>
          <span>·</span>
          <span style={{ fontWeight: 600, color: '#1E2733' }}>{money(c.total_cost)}</span>
        </div>
        <div className="okb-card-row">
          {c.end_date && <span>Ends {c.end_date.slice(0, 10)}</span>}
          {deadlineBadge(c)}
        </div>
      </>
    );
  }

  return (
    <div>
      <OdooListView
        eyebrow="Marketing" title="Campaigns"
        count={rows ? `${rows.length} campaign${rows.length !== 1 ? 's' : ''}` : 'Loading…'}
        columns={columns} rows={rows || []} getRowKey={(c) => c.id}
        onRowClick={(c) => nav('/m/campaigns/' + c.id)}
        searchKeys={['name', 'brand_name']}
        filters={filters} groupBy={groupBy}
        views={['list', 'kanban']} view={view} onViewChange={setView}
        rightAction={perm.can_create && <button className="btn btn-dark" onClick={() => setModal(true)}><FiPlus /> New campaign</button>}
        renderBody={view === 'kanban' ? (
          <OdooKanban
            columns={COLUMNS} groupField="status" records={rows || []}
            getKey={(c) => c.id} onCardClick={(c) => nav('/m/campaigns/' + c.id)}
            renderCard={renderCard} onMove={moveStatus} canMove={perm.can_edit}
          />
        ) : null}
      />
      {modal && <CampaignModal brands={brands} onClose={() => setModal(false)} onSaved={() => { setModal(false); flash('Campaign created'); load(); }} />}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

export function CampaignModal({ campaign, initialTargets, brands: brandsProp, onClose, onSaved }) {
  const edit = !!campaign;
  const [brands, setBrands] = useState(brandsProp || []);
  const [f, setF] = useState({
    name: campaign?.name || '', objective: campaign?.objective || '', brand_id: campaign?.brand_id || '',
    status: campaign?.status || 'planning',
    start_date: campaign?.start_date?.slice(0, 10) || '', end_date: campaign?.end_date?.slice(0, 10) || '',
  });
  const [targets, setTargets] = useState(
    initialTargets && initialTargets.length ? initialTargets.map((t) => ({ ...t })) : [{ metric: '', target_value: '', unit: '', achievement_value: '' }]);
  const [error, setError] = useState(''); const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  useEffect(() => { if (!brandsProp) api('/brands/options').then(setBrands).catch(() => {}); }, [brandsProp]);
  function setTarget(i, k, v) { const n = targets.slice(); n[i] = { ...n[i], [k]: v }; setTargets(n); }
  function addTarget() { setTargets([...targets, { metric: '', target_value: '', unit: '', achievement_value: '' }]); }
  function removeTarget(i) { setTargets(targets.filter((_, idx) => idx !== i)); }
  async function save() {
    setError(''); setBusy(true);
    const body = { ...f, brand_id: f.brand_id || null, targets: targets.filter((t) => t.metric && t.metric.trim()) };
    try { if (edit) await api('/campaigns/' + campaign.id, { method: 'PUT', body }); else await api('/campaigns', { method: 'POST', body }); onSaved(); }
    catch (e) { setError(e.message); setBusy(false); }
  }
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 620 }} onClick={(e) => e.stopPropagation()}>
        <h2>{edit ? 'Edit campaign' : 'New campaign'}</h2>
        <div className="field"><label>Campaign name</label>
          <input value={f.name} autoFocus onChange={set('name')} placeholder="e.g. SmartBoard Fabricator Meet — Kochi" /></div>
        <div className="grid2">
          <div className="field"><label>Brand</label>
            <select value={f.brand_id} onChange={set('brand_id')}>
              <option value="">— Select brand —</option>
              {brands.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
            </select></div>
          <div className="field"><label>Status</label>
            <select value={f.status} onChange={set('status')}>
              <option value="planning">Planning</option><option value="active">Active</option><option value="completed">Completed</option>
            </select></div>
        </div>
        <div className="field"><label>Objective</label>
          <input value={f.objective} onChange={set('objective')} placeholder="What is this campaign trying to achieve?" /></div>
        <label style={{ fontSize: 12.5, fontWeight: 600, color: '#4A453E' }}>Targets</label>
        <div className="targets-block">
          {targets.map((t, i) => (
            <div className="target-row" key={i}>
              <input placeholder="Metric (e.g. Leads)" value={t.metric || ''} onChange={(e) => setTarget(i, 'metric', e.target.value)} />
              <input type="number" placeholder="Target" value={t.target_value || ''} onChange={(e) => setTarget(i, 'target_value', e.target.value)} />
              <input placeholder="Unit" value={t.unit || ''} onChange={(e) => setTarget(i, 'unit', e.target.value)} />
              {edit && <input type="number" placeholder="Achieved" value={t.achievement_value || ''} onChange={(e) => setTarget(i, 'achievement_value', e.target.value)} />}
              <button className="btn btn-sm btn-danger" onClick={() => removeTarget(i)} title="Remove"><FiTrash2 /></button>
            </div>
          ))}
          <button className="btn btn-sm" onClick={addTarget} style={{ marginTop: 4 }}><FiPlus /> Add target</button>
        </div>
        <div className="grid2" style={{ marginTop: 14 }}>
          <div className="field"><label>Start date</label><input type="date" value={f.start_date} onChange={set('start_date')} /></div>
          <div className="field"><label>End date</label><input type="date" value={f.end_date} onChange={set('end_date')} /></div>
        </div>
        {error && <div className="inline-error">{error}</div>}
        <div className="modal-actions">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-dark" onClick={save} disabled={busy || !f.name.trim()}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
